import { useEffect, useRef } from 'react';

/**
 * Animated topographic contour-line background.
 *
 * 3D simplex noise sampled at (uv * spatialScale, time * timeSpeed) → isolines via
 * fract-and-smoothstep on the continuous noise value. The third axis is time, which
 * is why the lines slowly bend and merge into each other rather than scrolling
 * rigidly. Anti-aliasing via fwidth() so lines stay crisp at any DPR.
 *
 * Black background, single blue accent.
 */

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;
uniform float u_time;
uniform vec2 u_resolution;
uniform float u_scale;
uniform float u_speed;
uniform float u_bands;
uniform float u_lineWidth;
uniform float u_spill;

// Simplex 3D noise (Ashima / Stefan Gustavson)
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

// fBM (fractal Brownian motion) on simplex — multi-octave for natural look
float fbm(vec3 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 4; i++) {
    v += amp * snoise(p);
    p *= 2.0;
    amp *= 0.5;
  }
  return v;
}

// 1D hash — pseudo-random value per oil-spill epoch
float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

// HSV → RGB for the oil-slick iridescence
vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

void main() {
  // Aspect-correct UV so lines aren't stretched on wide screens
  vec2 uv = (v_uv * u_resolution) / min(u_resolution.x, u_resolution.y);

  float n = fbm(vec3(uv * u_scale, u_time * u_speed));
  // isoline distance from band center, in band-units
  float bandPos = abs(fract(n * u_bands) - 0.5);
  float aa = fwidth(n * u_bands);
  // intensity: 1.0 on the line, 0.0 between
  float line = 1.0 - smoothstep(u_lineWidth - aa, u_lineWidth + aa, bandPos);

  // Subtle radial vignette so center pops a bit
  vec2 centered = v_uv - 0.5;
  float vignette = 1.0 - dot(centered, centered) * 0.6;

  vec3 background = vec3(0.0);
  vec3 lineColor = vec3(0.30, 0.65, 1.0);  // bright blue
  vec3 color = mix(background, lineColor, line * vignette);

  // ── Occasional iridescent contour-region bloom ─────────────────────
  // Not a separate overlaid blob — this fills ONE band of the existing
  // topographic field. The iridescent region's edges land exactly on the
  // isolines (AA'd), so it IS a contour region of the same animated topo,
  // and it morphs frame-to-frame because the noise it's cut from morphs.
  // A soft spatial weight keeps it on the right half near a hashed point;
  // that weight has no hard edge of its own — every visible boundary is
  // a contour line.
  if (u_spill > 0.5) {
    float period = 9.0;                   // seconds per spill epoch
    float epoch  = floor(u_time / period);
    float localT = fract(u_time / period); // 0..1 within the epoch

    // Hashed drift point on the RIGHT half of the screen.
    vec2 spillCenter = vec2(
      0.60 + 0.28 * hash11(epoch * 1.73 + 0.31),
      0.25 + 0.50 * hash11(epoch * 2.91 + 5.17)
    );

    // Bloom only lives in the middle of the epoch — fade in, hold, fade out
    float fade = smoothstep(0.12, 0.26, localT)
               * (1.0 - smoothstep(0.46, 0.60, localT));

    // Soft spatial weight — biases the spill near the center. NOT a shape:
    // its falloff is gentle and wide so it never reads as a circle edge.
    vec2 aspect = vec2(u_resolution.x / u_resolution.y, 1.0);
    float d = distance(v_uv * aspect, spillCenter * aspect);
    float locality = 1.0 - smoothstep(0.05, 0.24, d);

    // Sample the topo AT the spill center, then fill the contour band that
    // passes through it. The fill is bounded by the isolines themselves.
    // The sample time is FROZEN to this epoch (its peak-visibility instant)
    // so floor() can't snap targetBand to a neighbouring band mid-spill —
    // a live-animating nCenter is what made the iridescence jump around.
    // The per-pixel field still uses live u_time, so the chosen band keeps
    // morphing smoothly; only its identity is pinned for the spill's life.
    vec2 cUv = (spillCenter * u_resolution) / min(u_resolution.x, u_resolution.y);
    float frozenT = (epoch + 0.36) * period;
    float nCenter = fbm(vec3(cUv * u_scale, frozenT * u_speed));
    float targetBand = floor(nCenter * u_bands) + 0.5;

    float nb = n * u_bands;
    float aaB = fwidth(nb);
    float bandFill = 1.0 - smoothstep(0.5 - aaB, 0.5 + aaB, abs(nb - targetBand));

    float spillMask = bandFill * locality * fade;

    // Oil-slick iridescence — hue sweeps across the band width
    float hue = fract((nb - targetBand) * 0.9 + u_time * 0.06 + epoch * 0.37);
    vec3 oil = hsv2rgb(vec3(hue, 0.78, 1.0));

    // Fill the contour region; the isolines pop brighter inside it
    color = mix(color, oil, spillMask * 0.7);
    color = mix(color, min(oil + 0.25, vec3(1.0)), line * spillMask);
  }

  outColor = vec4(color, 1.0);
}
`;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`Shader compile failed: ${log}`);
  }
  return sh;
}

interface TopographicBackgroundProps {
  className?: string;
  /** Spatial frequency — higher = more, tighter contours. Default 1.6. */
  scale?: number;
  /** Morph rate — higher = faster animation. Default 0.02. */
  speed?: number;
  /** Number of isolines across the noise range. Default 14. */
  bands?: number;
  /** Visual thickness of each line (0..0.5). Default 0.025. */
  lineWidth?: number;
  /** Enable the occasional iridescent "oil-spill" bloom. Default false. */
  spill?: boolean;
}

export function TopographicBackground({
  className,
  scale = 1.6,
  speed = 0.02,
  bands = 14,
  lineWidth = 0.025,
  spill = false,
}: TopographicBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl2', { antialias: false, premultipliedAlpha: false });
    if (!gl) {
      console.warn('[TopographicBackground] WebGL2 unavailable; falling back to plain background.');
      return;
    }

    let vs: WebGLShader | null = null;
    let fs: WebGLShader | null = null;
    let prog: WebGLProgram | null = null;
    let buf: WebGLBuffer | null = null;
    let raf = 0;
    let ro: ResizeObserver | null = null;

    try {
      vs = compile(gl, gl.VERTEX_SHADER, VERT_SRC);
      fs = compile(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
      prog = gl.createProgram()!;
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        console.warn('[TopographicBackground] program link failed:', gl.getProgramInfoLog(prog));
        return;
      }
      gl.useProgram(prog);

      // Full-screen triangle (3 verts, no element buffer)
      buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

      const uTime = gl.getUniformLocation(prog, 'u_time');
      const uRes = gl.getUniformLocation(prog, 'u_resolution');
      const uScale = gl.getUniformLocation(prog, 'u_scale');
      const uSpeed = gl.getUniformLocation(prog, 'u_speed');
      const uBands = gl.getUniformLocation(prog, 'u_bands');
      const uLineWidth = gl.getUniformLocation(prog, 'u_lineWidth');
      const uSpill = gl.getUniformLocation(prog, 'u_spill');
      gl.uniform1f(uScale, scale);
      gl.uniform1f(uSpeed, speed);
      gl.uniform1f(uBands, bands);
      gl.uniform1f(uLineWidth, lineWidth);
      gl.uniform1f(uSpill, spill ? 1.0 : 0.0);

      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const resize = () => {
        const w = Math.floor(canvas.clientWidth * dpr);
        const h = Math.floor(canvas.clientHeight * dpr);
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
          gl.viewport(0, 0, w, h);
        }
      };
      resize();
      ro = new ResizeObserver(resize);
      ro.observe(canvas);

      const start = performance.now();
      const render = () => {
        try {
          const t = (performance.now() - start) / 1000;
          gl.uniform1f(uTime, t);
          gl.uniform2f(uRes, canvas.width, canvas.height);
          gl.drawArrays(gl.TRIANGLES, 0, 3);
          raf = requestAnimationFrame(render);
        } catch (err) {
          console.warn('[TopographicBackground] render frame failed; halting animation:', err);
        }
      };
      render();
    } catch (err) {
      console.warn('[TopographicBackground] setup failed; rendering plain canvas:', err);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
      if (prog) gl.deleteProgram(prog);
      if (vs) gl.deleteShader(vs);
      if (fs) gl.deleteShader(fs);
      if (buf) gl.deleteBuffer(buf);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} style={{ display: 'block' }} />;
}
